import type { SitePhotoRepository } from "~application/sites/ports/site-photo-repository.ts";
import type { Site } from "~domain/sites/models/site-model.ts";
import type { SitePhoto, SitePhotoBinary } from "~domain/sites/models/site-photo-model.ts";
import type { BackofficeApi } from "~infrastructure/api/backoffice/http/backoffice-tsrest-client.ts";

import {
  DomainError,
  DomainErrorCode,
  DomainTransportError,
  DomainUnauthorizedError,
} from "~domain/global/errors/domain-error.ts";
import { SitePhotoMapper } from "~infrastructure/sites/mappers/site-photo-mapper.ts";

export class BackofficeSitePhotoRepository implements SitePhotoRepository {
  constructor(private readonly backofficeApi: BackofficeApi) {}

  async findSitePhotos(site: Pick<Site, "siteId">): Promise<Array<SitePhoto>> {
    const response = await this.backofficeApi.sites.findSitePhotos({
      params: {
        siteId: site.siteId,
      },
    });

    if (response.status === 200) {
      return SitePhotoMapper.fromBackofficeSitePhotosToDomain(response.body);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findSitePhotoBinary(sitePhoto: SitePhoto, format: "THUMBNAIL" | "HD"): Promise<SitePhotoBinary> {
    const response = await this.backofficeApi.sites.findSitePhoto({
      params: {
        photoId: sitePhoto.id,
      },
      query: {
        format: format,
      },
    });

    if (response.status === 200) {
      return {
        ...sitePhoto,
        binary: response.body,
      };
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }
}
